import { Check, X } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { SignalStats } from "@/queries/signals";

const requestCountFormatter = Intl.NumberFormat("en-US", {
  notation: "compact",
});

interface SignalCardStatsProps {
  stats?: SignalStats;
  isLoading?: boolean;
}

const SignalCardStats = ({ stats, isLoading = false }: SignalCardStatsProps) => {
  if (isLoading) {
    return (
      <div className="flex shrink-0 items-center gap-1.5">
        <Skeleton className="h-5 w-8 rounded-full" />
        <Skeleton className="h-5 w-10 rounded-full" />
        <Skeleton className="h-5 w-10 rounded-full" />
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { processed_requests_count, true_count, false_count } = stats;
  const labeledTotal = true_count + false_count;
  const trueRatio = labeledTotal > 0 ? Math.round((true_count / labeledTotal) * 100) : null;
  const falseRatio = labeledTotal > 0 ? 100 - (trueRatio ?? 0) : null;

  const hasRequestCount = processed_requests_count > 0;
  const hasRatios = labeledTotal > 0 && trueRatio !== null && falseRatio !== null;

  if (!hasRequestCount && !hasRatios) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {hasRequestCount ? (
        <BasicTooltip
          content={`The signal evaluated ${processed_requests_count.toLocaleString()} ${
            processed_requests_count === 1 ? "request" : "requests"
          }`}
        >
          <span className="rounded-full bg-gray-300 px-2 py-0.5 text-xs font-medium text-gray-800">
            {requestCountFormatter.format(processed_requests_count)}
          </span>
        </BasicTooltip>
      ) : null}
      {hasRatios ? (
        <>
          <BasicTooltip content={`${trueRatio}% of requests evaluated true`}>
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              <Check className="h-3 w-3 shrink-0" aria-hidden="true" />
              {trueRatio}%
            </span>
          </BasicTooltip>
          <BasicTooltip content={`${falseRatio}% of requests evaluated false`}>
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              <X className="h-3 w-3 shrink-0" aria-hidden="true" />
              {falseRatio}%
            </span>
          </BasicTooltip>
        </>
      ) : null}
    </div>
  );
};

export default SignalCardStats;
